import { attr, belongsTo, hasMany } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type NotificationProcessedJournalEvent from "parog-web/models/notification-processed-journal-event";
import tWithDefault from "parog-web/utils/t-with-default";
import type CommentWrittenJournalEvent from "./comment-written-journal-event";
import type Patient from "./patient";

//---- Automatically generated, don't update this part ----

export default class NotificationTriggeredJournalEvent extends JournalEvent {
  static override type = "notification-triggered-journal-event";

  /**
   * The label, suitable for a user.
   */
  @attr("string") declare label: string;

  /**
   * The label, without a value, if any
   */
  @attr("string") declare labelWithoutValue: string;

  /**
   * The value extracted from the label
   */
  @attr("string") declare labelValue: string;

  @attr("date") declare triggeringJournalEventOccurredAt: Date;

  @attr("string") declare triggeringJournalEventId: string;

  /**
   * This notification is associated to a journal event that threw alert
   */
  @attr("boolean", {
    allowNull: true
  })
  declare triggeredByAnAlertingJournalEvent: boolean;

  @attr("string") declare apimailSender: string;

  @attr("string") declare prescriptor: string;

  static override validations(intl: IntlService) {
    return {
      triggeringJournalEvent: validatePresence({
        presence: true,
        description: intl.t(
          "validations.notification-triggered-journal-event.triggering-journal-event"
        )
      }),
      label: validatePresence({
        presence: true,
        description: intl.t(
          "validations.notification-triggered-journal-event.label"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.notification-triggered-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  get icon():
    | "bio"
    | "chat"
    | "survey"
    | "stetoscope"
    | "document-plus"
    | "cr"
    | "pill" {
    switch (
      (this.triggeringJournalEvent.constructor as typeof JournalEvent).type
    ) {
      case "bio-exam-results-received-journal-event":
        return "bio";
      case "patient-chat-message-sent-journal-event":
        return "chat";
      case "survey-completed-journal-event":
      case "compound-survey-completed-journal-event":
        return "survey";
      case "weight-measured-journal-event":
        return "stetoscope";
      case "file-uploaded-journal-event":
        return "document-plus";
      case "consultation-report-written-journal-event":
        return "cr";
      default:
        return "stetoscope";
    }
  }
  @belongsTo("journal-event", {
    async: false,
    inverse: "notificationTriggeredJournalEvents",
    polymorphic: true,
    as: "journal-event"
  })
  declare triggeringJournalEvent: JournalEvent;

  @belongsTo("notification-processed-journal-event", {
    async: false,
    inverse: "notificationTriggeredJournalEvent"
  })
  declare notificationProcessedJournalEvent: NotificationProcessedJournalEvent;

  @hasMany("comment-written-journal-event", {
    async: false,
    inverse: "originJournalEvent",
    as: "journal-event",
    polymorphic: true
  })
  declare commentWrittenJournalEvents: CommentWrittenJournalEvent[];

  @belongsTo("patient", {
    async: false,
    inverse: "notificationTriggeredJournalEvents"
  })
  declare patient: Patient;

  @attr("string") declare triggeringJournalEventType: string;

  get isUnprocessed(): boolean {
    return !this.notificationProcessedJournalEvent;
  }

  get hasBeenProcessed(): boolean {
    return !this.isUnprocessed;
  }

  override get isAlertingJournalEvent(): boolean {
    return this.triggeredByAnAlertingJournalEvent;
  }

  get isBioExamNotification(): boolean {
    return (
      this.triggeringJournalEventType === "BioExamResultsReceivedJournalEvent"
    );
  }

  /**
   * Process this notification.
   *
   * This creates a notification-processed-journal-event linked to this event
   */
  async processNotification(
    source: string | null = null
  ): Promise<NotificationProcessedJournalEvent> {
    const notificationProcessedJournalEvent = this.store.createRecord(
      "notification-processed-journal-event",
      {
        patient: this.patient,
        occurredAt: new Date(),
        source,
        notificationTriggeredJournalEvent: this
      }
    );
    return await notificationProcessedJournalEvent.save();
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "notification-triggered-journal-event": NotificationTriggeredJournalEvent;
  }
}

declare module "semeia-api" {
  export interface ApiFilterRegistry {
    "notification-triggered-journal-event": {
      non_processed: "";
      for_current_medic?: "";
    };
  }
}
